import { Action, ThunkAction, createAction } from '@reduxjs/toolkit';
import { ErrorCode } from '../errors/types';
import { getErrorInfoByCode } from '../errors/utils';
import waitForActionDispatch from '../redux/waitForActionDispatch';
import { selectConfirmationDialog } from './selectors';
import { ConfirmDialogState, DialogsRootState } from './types';

/**
 * Action for displaying a confirm dialog asking the user for a OK/Cancel
 * or Yes/No question or confirmation.
 */
export const showConfirmationDialog = createAction<ConfirmDialogState>('dialogs/showConfirmation');

/**
 * Action for closing a dialog asking the user
 * for a OK/Cancel or Yes/No question or confirmation.
 * The payload boolean value should be true if OK was selected,
 * otherwise false.
 */
export const closeConfirmationDialog = createAction<boolean>('dialogs/closeConfirmation');

/**
 * Useful thunk that displays a confirm dialog asking the user for a OK/Cancel
 * or Yes/No question or confirmation. It waits until either one is selected,
 * returning a promise resulting in a boolean value accordingly.
 */
export function confirmWithDialog(
  dialog: ConfirmDialogState,
): ThunkAction<Promise<boolean>, DialogsRootState, unknown, Action<string>> {
  return async (dispatch, getState) => {
    const currentDialog = selectConfirmationDialog(getState());
    if (currentDialog != null) {
      throw new Error('Confirmation dialog is already open');
    }
    // Show the dialog
    dispatch(showConfirmationDialog(dialog));
    // Wait until dialog is closed
    const closeAction = await dispatch(waitForActionDispatch(closeConfirmationDialog));
    return closeAction.payload;
  };
}

let redirecting = false;

export function confirmUnauthorizedAndRedirect(
  redirectUrl = '',
): ThunkAction<Promise<void>, any, unknown, Action<string>> {
  return async (dispatch) => {
    if (redirecting) {
      return;
    }
    const confirmed = await dispatch(
      confirmWithDialog({
        okText: 'Sign in',
        cancelText: 'Close',
        ...getErrorInfoByCode(ErrorCode.Unauthorized),
      }),
    );

    if (confirmed) {
      redirecting = true;
      if (redirectUrl === '') {
        window.location.reload();
      } else {
        window.location.href = redirectUrl;
      }
    }
  };
}
